import { useLingui } from "@lingui/react/macro";
import { AccessibleIcon } from "@offstreetparking/design-system/accessibility";
import { CircleCheckIcon } from "@offstreetparking/design-system/icons/CircleCheckIcon";
import { ErrorIcon } from "@offstreetparking/design-system/icons/ErrorIcon";
import { Heading } from "@offstreetparking/design-system/typography";
import { PermitStatus } from "@offstreetparking/domain/value/permits";
import { DateTime } from "luxon";
import { useCallback } from "react";
import { offstreetClient } from "../../../../api/client";
import { Confirmation } from "../../../dataDisplay/Confirmation";
import { ConfirmationSkeleton } from "../../../dataDisplay/Confirmation/ConfirmationSkeleton";
import { KioskCountdown } from "../../../dataDisplay/Confirmation/components/KioskCountdown";
import { ServerErrorAlert } from "../../../forms/Alert";
import { useKiosk } from "../../../providers/KioskProvider";
import { usePermitFormData } from "../../PermitContextProvider";
import { usePermitWizardSettings } from "../../PermitContextProvider/PermitContextProvider";
type ConfirmationStepProps = {
	publicIdentifier: string;
	publicAccessToken?: string | null;
	fields?: {
		permitFieldId: number;
		value: string;
		label: string;
	}[];
	email?: string;
	phoneNumber?: string;
	addAnotherOption?: () => void;
};
export const ConfirmationStep = ({
	publicIdentifier,
	publicAccessToken,
	fields,
	email,
	phoneNumber,
	addAnotherOption,
}: ConfirmationStepProps) => {
	const { isKiosk } = useKiosk();
	const { t } = useLingui();
	const wizardSettings = usePermitWizardSettings();
	const permitFormData = usePermitFormData();
	const selectedChoice =
		wizardSettings.choices.find((choice) => choice.permitTypeId === permitFormData.parkingChoiceId) ??
		wizardSettings.choices[0];
	const headingRef = useCallback((node: HTMLDivElement | null) => {
		if (node) {
			node.focus();
		}
	}, []);
	const {
		data: permit,
		isPending,
		error,
	} = offstreetClient.permit.getPermitByPublicIdentifier.useQuery({
		publicIdentifier,
	});
	if (isPending) {
		return <ConfirmationSkeleton />;
	}
	if (error) {
		return <ServerErrorAlert serverError={error} />;
	}
	return (
		<div>
			{permit.permitStatus === PermitStatus.VOID ? (
				<div
					ref={headingRef}
					tabIndex={-1}
					className="sm:-mb-4 flex items-center justify-center gap-2 rounded-lg border border-critical bg-critical-faded pt-4 outline-none"
				>
					<AccessibleIcon>
						<ErrorIcon className="h-9 w-9 text-critical" />
					</AccessibleIcon>
					<Heading As="h2" kind="h2">
						{t`Permit Deleted`}
					</Heading>
				</div>
			) : (
				<div
					ref={headingRef}
					tabIndex={-1}
					className="sm:-mb-4 flex items-center justify-center gap-2 pt-4 outline-none"
				>
					<AccessibleIcon>
						<CircleCheckIcon className="h-9 w-9 text-success" />
					</AccessibleIcon>
					<Heading As="h2" kind="h2">
						{t`Parking Confirmed!`}
					</Heading>
				</div>
			)}
			<Confirmation
				{...permit}
				parkerTerms={permit.parkerTerms}
				email={email}
				phoneNumber={phoneNumber}
				permitIntervals={permit.permitIntervals.map((interval) => ({
					startDateTimeISO: interval.startDateTime,
					endDateTimeISO: interval.endDateTime,
					schedules:
						interval.schedules?.map((x) => ({
							startTime: DateTime.fromISO(x.startTime).toFormat("HH:mm:ss"),
							endTime: DateTime.fromISO(x.endTime).toFormat("HH:mm:ss"),
							day: x.day,
						})) ?? [],
				}))}
				instructions={permit.confirmationInstructions}
				publicIdentifier={publicIdentifier}
				isEditPermitDisabled={permit.wizardSettings.isEditPermitDisabled}
				isCancelPermitDisabled={permit.wizardSettings.isCancelPermitDisabled}
				maxVehicleCount={permit.wizardSettings.maxVehicleCount}
				isSmsEnabled={permit.smsExpiryNotifications}
				publicAccessToken={publicAccessToken}
				permitStatus={permit.permitStatus}
				fields={fields}
				addAnotherOption={addAnotherOption}
				printPermit={permit.company?.productFeatures?.printPermit ?? false}
				fullTermIntervals={selectedChoice?.context?.fixedIntervals}
			/>

			{isKiosk && <KioskCountdown />}
		</div>
	);
};
